import React, { useState } from 'react';
import { OnboardingData } from '../../types/onboarding';
import { SCIO_EXAM_DATES } from '../../services/onboardingPlan.constants';
import { getDaysUntilExam } from '../../services/onboardingPlan';
import { Clock } from 'lucide-react';

interface Props {
    onNext: (data: Partial<OnboardingData>) => void;
    onBack: () => void;
    initialData: Partial<OnboardingData>;
    isTransitioning?: boolean;
}

const OnboardingExamDate: React.FC<Props> = ({ onNext, onBack, initialData, isTransitioning }) => {
    const [examDate, setExamDate] = useState<string | undefined>(initialData.examDate);

    const handleSubmit = () => {
        onNext({ examDate });
    };

    return (
        <div className="space-y-6 sm:space-y-12 animate-in fade-in slide-in-from-right-4 duration-300">
            <div>
                <label className="block text-gray-900 dark:text-white font-bold text-2xl mb-2">
                    Konkrétní termín zkoušky SCIO (nepovinné)
                </label>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4 sm:mb-8">
                    Zvol si termín, abychom ti mohli ukazovat odpočet a přizpůsobit intenzitu studia
                </p>
                <div className="flex flex-col gap-2 sm:gap-3 max-w-md mx-auto">
                    {SCIO_EXAM_DATES.map((exam) => {
                        const daysLeft = getDaysUntilExam(exam.date);
                        const isSelected = examDate === exam.date;
                        const isPast = daysLeft !== null && daysLeft <= 0;

                        return (
                            <button
                                key={exam.date}
                                onClick={() => !isPast && setExamDate(exam.date)}
                                disabled={isPast}
                                className={`w-full px-6 py-3.5 rounded-xl border-2 border-b-[6px] transition-all duration-200 text-left ${isSelected
                                    ? 'border-sky-400 bg-sky-50 text-sky-600 shadow-sm translate-y-[2px]'
                                    : isPast
                                        ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                                        : 'border-gray-300 dark:border-gray-600 hover:border-brand-orange/50 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'
                                    } ${!isPast && 'active:border-b-2 active:translate-y-[6px]'}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className={`text-base font-bold ${isSelected ? 'text-sky-600' : 'text-gray-900 dark:text-gray-100'}`}>
                                            {exam.label}
                                        </span>
                                    </div>
                                    {daysLeft !== null && daysLeft > 0 && (
                                        <span className={`text-sm font-bold px-3 py-1 rounded-full ${daysLeft <= 14 ? 'bg-red-100 text-red-600' :
                                            daysLeft <= 30 ? 'bg-yellow-100 text-yellow-600' :
                                                'bg-green-100 text-green-600'
                                            }`}>
                                            <Clock className="w-3 h-3 inline mr-1" />
                                            {daysLeft} dní
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                    <button
                        onClick={() => setExamDate(undefined)}
                        className={`w-full px-6 py-3.5 rounded-xl border-2 border-b-[6px] transition-all duration-200 text-left ${!examDate
                            ? 'border-sky-400 bg-sky-50 text-sky-600 shadow-sm translate-y-[2px]'
                            : 'border-gray-300 dark:border-gray-600 hover:border-brand-orange/50 hover:bg-gray-50 dark:hover:bg-gray-800'
                            } active:border-b-2 active:translate-y-[6px]`}
                    >
                        <span className={`text-base font-bold ${!examDate ? 'text-sky-600' : 'text-gray-900 dark:text-gray-100'}`}>
                            Nevím / Nechci zadat
                        </span>
                    </button>
                </div>
            </div>

            <div className="max-w-md mx-auto">
                <button
                    onClick={handleSubmit}
                    disabled={isTransitioning}
                    className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white text-lg font-bold py-3.5 rounded-xl transition-all border-b-[6px] border-orange-500 active:border-b-2 active:translate-y-[4px] disabled:opacity-50 disabled:cursor-not-allowed mt-4 shadow-orange-500/20 shadow-lg"
                >
                    Vytvořit můj plán
                </button>
            </div>
        </div>
    );
};

export default OnboardingExamDate;
